import { UIText1 } from '@cian/ui-kit';
import { numberToPrettyString, shortenNumber } from '@cian/utils';

import { type FC, type JSX, useMemo } from 'react';

import { rubleSymbol, squareMeterShort } from 'shared/constants/symbols';

import styles from './RoomsList.css';

export type RoomsInfoItem = {
  /** Кол-во комнат (0 и 9 - студия) **/
  roomsCount: number;
  /** Минимальная стоимость **/
  minPrice: number;
  /** Минимальная площадь **/
  minSquare?: number | null;
};

type RoomsListProps = {
  list: RoomsInfoItem[];
  /** @description Максимальное количество элементов в списке */
  maxItems: number;
};

export const RoomsList: FC<RoomsListProps> = ({ list, maxItems }) => {
  const listItemsView = useMemo<JSX.Element[]>(() => {
    const studios = Array.of<RoomsInfoItem>();
    const regularRooms = Array.of<RoomsInfoItem>();

    // Группируем элементы: студии (0, 9) отдельно, остальные отдельно
    for (const item of list) {
      if (item.roomsCount === 0 || item.roomsCount === 9) {
        studios.push(item);
      } else {
        regularRooms.push(item);
      }
    }

    // Схлопываем студии в один элемент с минимальными значениями
    const collapsedStudio =
      studios.length > 0
        ? {
            minPrice: Math.min(...studios.map(item => item.minPrice)),
            minSquare: studios.some(item => item.minSquare)
              ? Math.min(...studios.map(item => item.minSquare || Infinity))
              : null,
            roomsCount: 0,
          }
        : null;

    // Сортируем обычные комнаты по количеству
    const sortedRegularRooms = regularRooms.sort((a, b) => a.roomsCount - b.roomsCount);

    // Объединяем результат
    const processedList = collapsedStudio ? [collapsedStudio, ...sortedRegularRooms] : sortedRegularRooms;

    return processedList.slice(0, maxItems).map(({ roomsCount, minPrice, minSquare }, index) => {
      const squareText = minSquare ? ` от ${numberToPrettyString(minSquare)} ${squareMeterShort}` : '';

      const countText = roomsCount === 0 ? `Студия${squareText}` : `${roomsCount}-комн.${squareText}`;

      const fromPriceText = minPrice
        ? `от ${shortenNumber(minPrice, {
            billionPostfix: 'млрд',
            millionPostfix: 'млн',
            separator: ',',
          })} ${rubleSymbol}`
        : 'Нет данных';

      return (
        <li className={styles['item']} data-testid="rooms-list-item" key={index}>
          <UIText1 data-testid="rooms-list-item-count">{countText}</UIText1>
          <UIText1 data-testid="rooms-list-item-price">{fromPriceText}</UIText1>
        </li>
      );
    });
  }, [list, maxItems]);

  return (
    <ul className={styles['container']} data-testid="rooms-list">
      {listItemsView}
    </ul>
  );
};
